'use client'

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/lib/stores/auth-store'
import i18n from '@/lib/i18n'
import { identifyUser, setAnalyticsMentorId } from '@/lib/analytics'
import { useThemeStore } from '@/lib/stores/theme-store'
import { getWhiteLabelDomain } from '@/lib/utils'

/**
 * Chama `umami.identify()` sempre que a sessão do usuário mudar.
 *
 * - No-op em SSR.
 * - Se o tracker ainda não carregou, fica tentando até ~8s.
 * - Guarda contra chamadas repetidas para o mesmo par usuário + mentor; re-identifica
 *   quando o mentor_id do tema fica disponível depois do usuário.
 */
export function useUmamiIdentify() {
  const user = useAuthStore((s) => s.user)
  const mentorId = useThemeStore((s) => s.currentTheme?.mentor_id)
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    setAnalyticsMentorId(mentorId)
  }, [mentorId])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!user?.id) {
      lastKeyRef.current = null
      return
    }
    const key = `${user.id}:${mentorId ?? ''}`
    if (lastKeyRef.current === key) return

    const host = window.location?.host || ''
    const whitelabelDomain = getWhiteLabelDomain(host)

    const payload = {
      email: user.email,
      name: user.name,
      mentor_id: mentorId || undefined,
      role: Array.isArray(user.roles) ? user.roles.join(',') : undefined,
      is_whitelabel: !!whitelabelDomain,
      whitelabel_domain: whitelabelDomain || undefined,
      locale: i18n?.language,
      app: 'mentorfy-2.0',
    }

    const fire = () => {
      identifyUser(user.id, payload)
      lastKeyRef.current = key
    }

    if (window.umami) {
      fire()
      return
    }

    let attempts = 0
    const interval = window.setInterval(() => {
      attempts += 1
      if (window.umami) {
        fire()
        window.clearInterval(interval)
      } else if (attempts > 40) {
        window.clearInterval(interval)
      }
    }, 200)
    return () => window.clearInterval(interval)
  }, [user?.id, user?.email, mentorId])
}
